export interface DeferredSubmit {
    /**
     * Marks the current submit as still in flight and hands back a promise that
     * resolves once its real outcome is known. Deferring again while one is
     * already outstanding returns that same promise, rather than replacing the
     * resolver and orphaning a promise a caller is already awaiting.
     */
    defer: () => Promise<boolean>;
    /**
     * Reports the outcome of the outstanding submit. A no-op when nothing is
     * pending, since the save path is also reached by flows that never deferred.
     */
    settle: (saved: boolean) => void;
}

/**
 * Tracks a product submit whose outcome is not known when the form handler
 * returns, because a second dialog decides whether the save actually happens.
 *
 * Without this, such a submit has to report "not saved" straight away, which is
 * indistinguishable from a genuine failure and makes callers waiting on the
 * save skip their follow-up action for good.
 *
 * @return {DeferredSubmit} A tracker for one submit at a time.
 */
export const createDeferredSubmit = (): DeferredSubmit => {
    let pending: Promise<boolean> | null = null;
    let report: ((saved: boolean) => void) | null = null;

    return {
        defer: () => {
            if (pending === null) {
                pending = new Promise<boolean>((resolve) => {
                    report = resolve;
                });
            }

            return pending;
        },
        settle: (saved: boolean) => {
            const resolve = report;

            pending = null;
            report = null;

            resolve?.(saved);
        },
    };
};
